/**
 * GuestLoginModal — Modal de connexion "Invité" pour la Caisse Pro.
 *
 * Workflow :
 *  1. L'invité saisit le code reçu de l'Admin + choisit son rôle (Coach Jeux / Resp Op)
 *  2. POST /api/guest/login-request → renvoie un request_id en status "pending"
 *  3. Polling toutes les 3s sur /api/guest/request-status/{id}
 *  4. Quand status passe à "approved" → onApproved({ user })
 *  5. Si "rejected" → message d'erreur et reset
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  KeyRound,
  UserCheck,
  Gamepad2,
  ShieldCheck,
  XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: "manager", label: "Resp Op / Caisse", icon: ShieldCheck, color: "text-amber-400" },
  { value: "coach_jeux", label: "Coach Jeux", icon: Gamepad2, color: "text-teal-400" },
];

export default function GuestLoginModal({ open, onOpenChange, onApproved }) {
  const [code, setCode] = useState("");
  const [chosenRole, setChosenRole] = useState("manager");
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [status, setStatus] = useState(null);
  const [rejected, setRejected] = useState(false);
  const pollRef = useRef(null);

  // Reset état quand on ferme
  useEffect(() => {
    if (!open) {
      setCode("");
      setChosenRole("manager");
      setRequestId(null);
      setStatus(null);
      setRejected(false);
      setBusy(false);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, [open]);

  // Polling du statut
  useEffect(() => {
    if (!requestId) return undefined;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API}/guest/request-status/${requestId}`, { timeout: 6000 });
        if (cancelled) return;
        const s = r.data?.status;
        setStatus(s);
        if (s === "approved") {
          const approvedRole = r.data.approved_role || chosenRole;
          const label = r.data.label || "Invité";
          // Construit un user "guest" virtuel
          const guestUser = {
            id: `guest_${requestId.slice(0, 8)}`,
            username: `${label} (Invité)`,
            full_name: `${label} (Invité)`,
            role: approvedRole,
            is_guest: true,
            guest_request_id: requestId,
          };
          if (pollRef.current) clearInterval(pollRef.current);
          toast.success("Accès invité approuvé · Bienvenue !");
          onApproved?.(guestUser);
        } else if (s === "rejected") {
          setRejected(true);
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch (e) {
        if (!cancelled) console.warn("Guest poll error", e?.message);
      }
    };
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 3000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [requestId, chosenRole, onApproved]);

  const submit = async () => {
    const clean = (code || "").trim();
    if (!clean) {
      toast.error("Saisis le code reçu de l'Admin");
      return;
    }
    setBusy(true);
    setRejected(false);
    try {
      const body = {
        code: clean,
        chosen_role: chosenRole,
        device_label: (navigator?.userAgent || "").slice(0, 60),
      };
      const r = await axios.post(`${API}/guest/login-request`, body, { timeout: 8000 });
      setRequestId(r.data.request_id);
      setStatus("pending");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur — code invalide ou réseau";
      toast.error(detail);
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (requestId) {
      try {
        await axios.post(`${API}/guest/logout/${requestId}`).catch(() => {});
      } catch {}
    }
    onOpenChange(false);
  };

  const isPending = requestId && status === "pending" && !rejected;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && requestId) { cancel(); } else { onOpenChange(v); } }}>
      <DialogContent
        className="bg-slate-900 border-amber-500/30 text-white max-w-md"
        data-testid="guest-login-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <KeyRound className="w-5 h-5" /> Accès Invité
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Saisis le code à usage unique remis par l'Administrateur. Une notification lui sera envoyée pour validation.
          </DialogDescription>
        </DialogHeader>

        {!isPending && !rejected && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Rôle demandé</Label>
              <div className="grid grid-cols-2 gap-2">
                {ROLE_OPTIONS.map(({ value, label, icon: Icon, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setChosenRole(value)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition ${
                      chosenRole === value
                        ? "bg-amber-500/15 border-amber-500/60"
                        : "bg-slate-800/60 border-slate-700 hover:border-slate-500"
                    }`}
                    data-testid={`guest-role-${value}`}
                  >
                    <Icon className={`w-5 h-5 ${color}`} />
                    <span className="text-xs font-medium text-white">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Code Invité</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={12}
                placeholder="ex: 123456"
                className="bg-slate-800 border-slate-700 text-white text-center text-2xl tracking-[0.4em] font-mono h-14"
                data-testid="guest-code-input"
                onKeyDown={(e) => e.key === "Enter" && submit()}
              />
            </div>

            <Button
              onClick={submit}
              disabled={busy || !code.trim()}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold h-11"
              data-testid="guest-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" />}
              Demander l'accès
            </Button>

            <p className="text-slate-500 text-[11px] text-center">
              Session valable jusqu'à la fin de la journée ou jusqu'à révocation par l'Admin.
            </p>
          </div>
        )}

        {isPending && (
          <div className="space-y-4 py-2">
            <div className="flex flex-col items-center text-center gap-2 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
              <p className="text-amber-200 font-semibold">En attente de l'Administrateur…</p>
              <p className="text-slate-400 text-xs">
                Une notification rouge clignote sur son écran. Patiente quelques secondes.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={cancel}
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="guest-cancel-btn"
            >
              Annuler la demande
            </Button>
          </div>
        )}

        {rejected && (
          <div className="space-y-4 py-2">
            <div className="flex flex-col items-center text-center gap-2 p-4 rounded-xl bg-rose-500/10 border border-rose-500/30">
              <XCircle className="w-8 h-8 text-rose-400" />
              <p className="text-rose-200 font-semibold">Accès refusé</p>
              <p className="text-slate-400 text-xs">
                L'Administrateur a refusé ta demande. Rapproche-toi de lui pour comprendre.
              </p>
            </div>
            <Button
              onClick={() => onOpenChange(false)}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white"
            >
              Fermer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
